import React, {useState, useRef, useCallback, useEffect} from "react"
import {useHistory} from "react-router-dom"
import cx from "classnames"
import FullCalendar from "@fullcalendar/react"
import {get} from "lodash"
import "moment/min/locales"
import {useVisible, useGetCalendarEvents} from "hooks"
import {attendanceService, calendarService} from "services"
import {handleError} from "helpers"
import {Calendar} from "../Calendar"
import {ChooseRecurringPopup} from "components/Calendar/Common"
import {CalendarEventOption, RecurringPopupType} from "types/calendar"
import styles from "./HomeCalendar.module.css"
import {ShiftWorkWeek} from "types/attendance"
import {Calendar as FullCalendarView} from "components/Calendar"

type Props = {
    customCalendarHeight?: number
    userId?: number
    showStatsInfo?: boolean
    showFullCalendar?: boolean
    studentProfileId?: number
    shiftData?: {
        shiftWorkWeeks: ShiftWorkWeek[]
        startDate: string
        contractEndDate: string
        projectedEndDate: string
    }
}

export const HomeCalendar = (props: Props) => {
    const {customCalendarHeight, showStatsInfo, showFullCalendar, studentProfileId, shiftData} = props
    const [lessonStats, setLessonStats] = useState<{
        totalLessonAttended: number
        totalLessonMissed: number
        totalLesson: number
    }>()
    const calendarRef = useRef<FullCalendar>()
    const [tempEventId, setTempEventId] = useState(null)
    const [recurringPopupData, setRecurringPopupData] = useState({
        type: null,
        data: null
    })
    const chooseRecurringPopup = useVisible(false)
    const history = useHistory()

    const {events, isLoading, setEvents, reloadEvents, setDateRange} = useGetCalendarEvents({shiftData})

    const getLessonStats = useCallback(async () => {
        const params: any = {
            studentProfileIds: [studentProfileId]
        }
        const lessonsStats = await attendanceService.getRegistrarLessonStats(params)
        setLessonStats(lessonsStats)
    }, [studentProfileId])

    useEffect(() => {
        if (showStatsInfo && studentProfileId) {
            getLessonStats()
        }
    }, [showStatsInfo, getLessonStats, studentProfileId])

    const renderStatsInfo = useCallback(() => {
        const totalLesson = lessonStats?.totalLesson ?? 0
        const totalLessonAttended = lessonStats?.totalLessonAttended ?? 0
        const totalLessonMissed = lessonStats?.totalLessonMissed ?? 0
        return (
            <div className={styles.statsInfo}>
                <div className={styles.scheduled}>
                    <span className={styles.scheduledLabel}>Scheduled</span>
                    <div className={styles.scheduledStats}>
                        <span className={styles.scheduledText}>{totalLesson}</span>
                    </div>
                </div>
                <div className={styles.attended}>
                    <span className={styles.attendedLabel}>Attended</span>
                    <div className={styles.attendedStats}>
                        <span className={styles.attendedText}>{totalLessonAttended}</span>
                    </div>
                </div>
                <div className={styles.missed}>
                    <span className={styles.missedLabel}>Missed</span>
                    <div className={styles.missedStats}>
                        <span className={styles.missedText}>{totalLessonMissed}</span>
                    </div>
                </div>
                <div className={styles.remaining}>
                    <span className={styles.remainingLabel}>Remaining</span>
                    <div className={styles.remainingStats}>
                        <span className={styles.remainingText}>
                            {totalLesson - totalLessonAttended - totalLessonMissed}
                        </span>
                    </div>
                </div>
            </div>
        )
    }, [lessonStats])

    const onClickDeleteEvent = async (event) => {
        const eventId = +event.id
        const rrule = get(event, "_def.extendedProps.rrule")
        if (rrule) {
            setTempEventId(eventId)
            chooseRecurringPopup.open()
            setRecurringPopupData({
                type: RecurringPopupType.Delete,
                data: null
            })
            return
        }
        await deleteEvent(eventId)
    }

    const deleteEvent = async (eventId, target_filter = "only_this") => {
        try {
            const newEvents = events.filter((item) => item.id !== eventId)
            setEvents(newEvents)
            await calendarService.deleteEvent(eventId, {
                target_filter
            })
            reloadEvents()
        } catch (error) {
            handleError(error)
        }
    }

    const onClickUpdateGoingEvent = async (event, will_go) => {
        const eventId = +event.id
        const rrule = get(event, "_def.extendedProps.rrule")
        if (rrule) {
            setTempEventId(eventId)
            chooseRecurringPopup.open()
            setRecurringPopupData({
                type: RecurringPopupType.Update,
                data: {
                    will_go
                }
            })
            return
        }
        await updateGoingEvent(eventId, will_go)
    }

    const updateGoingEvent = async (object_id, will_go, target_filter = "only_this") => {
        try {
            await calendarService.updateStatus({
                object_id,
                target_filter,
                will_go
            })
            reloadEvents()
        } catch (error) {
            handleError(error)
        }
    }

    const onDateClick = () => {}

    const onClickEditEvent = (event) => {
        history.push(`/calendar-event/detail?id=${event.id}`)
    }

    const canShowEditIcon = (event) => {
        return (
            !event?.extendedProps?.isLesson &&
            !event?.extendedProps?.isAppointmentSlot &&
            !event?.extendedProps?.json_data?.isFromIntegration
        )
    }

    const canShowDelete = (event) => {
        return !event?.extendedProps?.isLesson && !event?.extendedProps?.json_data?.isFromIntegration
    }

    const getEventOptions = (): CalendarEventOption[] => {
        return [
            {
                title: "More detail",
                icon: "EDIT",
                action: onClickEditEvent,
                iconClassName: styles.eventPreviewHeaderEditIcon,
                canShow: canShowEditIcon
            },
            {title: "Delete event", icon: "DELETE", action: onClickDeleteEvent, canShow: canShowDelete},
            {title: "Going", icon: "CALENDAR_INPUT", action: onClickUpdateGoingEvent}
        ]
    }

    const onConfirmRecurring = async (target_filter) => {
        chooseRecurringPopup.close()
        if (recurringPopupData.type === RecurringPopupType.Delete) {
            await deleteEvent(tempEventId, target_filter)
        } else {
            await updateGoingEvent(tempEventId, recurringPopupData.data.will_go, target_filter)
        }
    }

    const eventOptions = getEventOptions()

    return (
        <div className={cx(styles.root, "home-calendar")}>
            <div className={styles.container}>
                {showStatsInfo ? renderStatsInfo() : null}
                {showFullCalendar ? (
                    <FullCalendarView
                        calendarRef={calendarRef}
                        events={events}
                        isLoading={isLoading}
                        eventOptions={eventOptions}
                        isShowGoing
                        customCalendarHeight={customCalendarHeight}
                        onDateClick={onDateClick}
                        onChangeDatesSet={(data) => setDateRange(data)}
                    />
                ) : (
                    <Calendar
                        calendarRef={calendarRef}
                        events={events}
                        isLoading={isLoading}
                        eventOptions={eventOptions}
                        isShowGoing
                        onDateClick={onDateClick}
                        onChangeDatesSet={(data) => setDateRange(data)}
                        dayHeaders={false}
                        customCalendarHeight={customCalendarHeight}
                    />
                )}
            </div>
            <ChooseRecurringPopup
                isShow={chooseRecurringPopup.isVisible}
                onClose={() => {
                    chooseRecurringPopup.close()
                }}
                type={recurringPopupData.type}
                onClickSave={onConfirmRecurring}
            />
        </div>
    )
}
